'use client'
import {
  CommitIcon,
  Cross1Icon,
  ExternalLinkIcon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { useReplicacheQuery } from '~/hooks-data/use-replicache-query'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useUserData } from '~/hooks-data/use-user-data'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { ActivityEvent } from '@basehub/data/replicache/index'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { ButtonLink, ButtonLinkProps } from '~/sotano-kit/primitives/button'
import { SYSTEM_AUTHORS } from '~/lib/system-authors'
import { ForkIcon } from '../../repositories/repository/fork-icon'

export const Activity: React.FC<{
  activity: ActivityEvent
}> = ({ activity }) => {
  const { teamSlug } = useDashboardSlugs()
  const timeDiff = useTimeDiff(new Date(activity.createdAt))
  const user = useUserData(activity.userId)
  const { data } = useTeamReplicache()
  const replicache = data?.replicache

  const repositoryQuery = useReplicacheQuery({
    replicache,
    fetcher: async (tx) =>
      (await teamSpaceMutators.getRepository(tx, activity.repoId)) ?? null,
    deps: [activity.repoId],
  })
  const repo = repositoryQuery.data

  const fullName = user.data ? makeFullNameFromClerkUser(user.data) : ''
  const isDeleted = user.data?.id === SYSTEM_AUTHORS.GHOST.id

  if (user.isLoading) {
    return (
      <div className="flex justify-between py-1">
        <div className="relative w-4/12 h-4 overflow-hidden border rounded border-white/10 bg-dark-shade before:absolute before:inset-0 before:-translate-x-full before:animate-loading-skeleton before:bg-loading" />
        <div className="relative w-2/12 h-4 overflow-hidden border rounded border-white/10 bg-dark-shade before:absolute before:inset-0 before:-translate-x-full before:animate-loading-skeleton before:bg-loading" />
      </div>
    )
  }

  return (
    <li
      className={clsx(
        'mr-6 block flex-col items-start gap-1 text-sm leading-5 text-dark-muted',
        activity.type === 'repo-transfer-failed' && 'text-red'
      )}
      key={activity.userId}
    >
      <div className="flex">
        <span className="mt-[3px] mr-2">
          {(() => {
            switch (activity.type) {
              case 'repo-commit':
                return <CommitIcon />
              case 'repo-create':
                return <PlusIcon />
              case 'repo-fork':
                return <ForkIcon />
              case 'repo-delete':
                return <TrashIcon />
              case 'repo-rename':
                return <Pencil1Icon />
              case 'repo-transfer':
                return <ExternalLinkIcon />
              case 'repo-transfer-failed':
                return <Cross1Icon />
              default:
                return <PlusIcon />
            }
          })()}
        </span>
        <p className="pr-2">
          {(() => {
            switch (activity.type) {
              case 'repo-commit':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    committed to{' '}
                    <CommitLink
                      href={
                        repo ? `/${teamSlug}/${repo.slug}/explore` : undefined
                      }
                    >
                      {activity.repoName}
                    </CommitLink>
                  </>
                )
              case 'repo-create':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    created repo{' '}
                    <CommitLink
                      href={repo ? `/${teamSlug}/${repo?.slug}/` : undefined}
                    >
                      {activity.repoName}
                    </CommitLink>
                  </>
                )
              case 'repo-delete':
                return (
                  <>
                    <CommitLink href={`/${user.data?.username}`}>
                      {fullName}
                    </CommitLink>{' '}
                    deleted repo{' '}
                    <CommitLink className="text-dark-faint">
                      {activity.repoName}
                    </CommitLink>
                  </>
                )
              case 'repo-rename':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    renamed repo{' '}
                    <CommitLink
                      href={repo ? `/${teamSlug}/${repo?.slug}/` : undefined}
                    >
                      "{activity.oldRepoName}"
                    </CommitLink>{' '}
                    to{' '}
                    <CommitLink
                      href={
                        repo ? `/${teamSlug}/${repo?.slug}/explore` : undefined
                      }
                    >
                      "{activity.newRepoName}"
                    </CommitLink>
                  </>
                )
              case 'repo-transfer':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    transferred repo{' '}
                    <CommitLink
                      href={repo ? `/${teamSlug}/${repo?.slug}/` : undefined}
                    >
                      {activity.repoName}
                    </CommitLink>{' '}
                    to{' '}
                    <CommitLink href={activity.teamSlug}>
                      {activity.teamName}
                    </CommitLink>
                  </>
                )
              case 'repo-transfer-failed':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    failed to transfer repo{' '}
                    <CommitLink
                      href={repo ? `/${teamSlug}/${repo?.slug}/` : undefined}
                    >
                      {activity.repoName}
                    </CommitLink>{' '}
                    to{' '}
                    <CommitLink href={activity.teamSlug}>
                      {activity.teamName}
                    </CommitLink>
                  </>
                )
              case 'repo-fork':
                return (
                  <>
                    <CommitLink
                      href={!isDeleted ? `/${user.data?.username}` : null}
                    >
                      {fullName}
                    </CommitLink>{' '}
                    forked repo{' '}
                    <CommitLink
                      href={repo ? `/${teamSlug}/${repo?.slug}/` : undefined}
                    >
                      {activity.repoName}
                    </CommitLink>{' '}
                    from{' '}
                    <CommitLink href={activity.sourceTeamSlug}>
                      {activity.sourceTeamName}
                    </CommitLink>
                  </>
                )
              default:
                return <>{fullName}</>
            }
          })()}
          {/* TODO: Add changes amount */}
        </p>
      </div>
      <div className="flex items-center ml-6 space-x-2 my-1">
        <ButtonLink
          unstyled
          href={`/${user.data?.username}`}
          className={isDeleted ? 'cursor-default pointer-events-none' : ''}
        >
          {user.data?.username}
        </ButtonLink>
        <p className="ml-auto whitespace-nowrap text-dark-faint">{timeDiff}</p>
      </div>
    </li>
  )
}

// get props of ButtonLink
type CommitLinkProps = Omit<ButtonLinkProps, 'unstyled' | 'href'> & {
  href?: string | null
}

const CommitLink = ({ className, href, ...props }: CommitLinkProps) => {
  return href ? (
    // @ts-ignore
    <ButtonLink
      className={clsx(
        'text-dark-dim transition-colors duration-200 hover:text-dark-bright',
        className
      )}
      href={href}
      unstyled
      {...props}
    />
  ) : (
    <span className={clsx('text-dark-faint', className)} {...props} />
  )
}
